import { useCallback, useMemo, useState } from "react";
import i18n from "../../../i18n";

export default function useCarousel<T>(items: T[], defaultIndex: number = 0, wrapAround: boolean = true) {
	const [currentIndex, setCurrentIndex] = useState<number>(defaultIndex);
	const [displayVal, setDisplayVal] = useState<string | number>(currentIndex + 1);

	const activeIndex = items.length > 0 ? Math.max(0, Math.min(currentIndex, items.length - 1)) : 0;

	const updateStates = useCallback((computeNext: (prev: number) => number) => {
		setCurrentIndex((prev) => {
			const next = computeNext(prev);
			setDisplayVal(next + 1);
			return next;
		});
	}, []);

	const handleBack = useCallback(() => {
		if (items.length === 0) return;

		updateStates((prev) => (wrapAround ? (prev - 1 + items.length) % items.length : Math.max(0, prev - 1)));
	}, [items.length, wrapAround, updateStates]);

	const handleNext = useCallback(() => {
		if (items.length === 0) return;
		updateStates((prev) => (wrapAround ? (prev + 1) % items.length : Math.min(items.length - 1, prev + 1)));
	}, [items.length, wrapAround, updateStates]);

	const handleJump = useCallback(
		(index: number) => {
			if (items.length === 0) return;
			// We only update state if the index is actually within the valid range
			if (index >= 0 && index < items.length) {
				updateStates(() => index);
			}
		},
		[items.length, updateStates],
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			// Number, not parseInt: the field is type="number", so the browser hands back
			// anything the spec calls a valid floating-point number, and parseInt("1e2")
			// stops at the "e" and reads 1. Number("") is 0 rather than NaN, so the empty
			// case has to be excluded by hand.
			const num = Number(val);

			// An empty or non-numeric value is an in-progress edit (e.g. clearing the
			// field before typing a new number) — echo it and wait for a real number
			// rather than jumping anywhere.
			if (val.trim() === "" || !Number.isFinite(num) || items.length === 0) {
				setDisplayVal(val);
				return;
			}

			// Clamp so the displayed value can never claim a page past the last file —
			// it and the file actually shown must always agree. Truncated first, because
			// "1.5" is a valid value for the field and a fractional index is not.
			// handleJump is the only writer of displayVal here: setting it separately as
			// well cost a second render on every keystroke.
			const clampedIndex = Math.min(Math.max(Math.trunc(num) - 1, 0), items.length - 1);
			handleJump(clampedIndex);
		},
		[items.length, handleJump],
	);

	const handleInputBlur = useCallback(() => {
		setDisplayVal(activeIndex + 1);
	}, [activeIndex]);

	const currentItem = useMemo(() => items[activeIndex], [items, activeIndex]);

	const atEnd = activeIndex === items.length - 1;
	const atStart = activeIndex === 0;

	const hasMultipleItems = items.length > 1;

	return {
		currentIndex: activeIndex,
		currentItem,
		hasMultipleItems,
		inputControl: {
			value: displayVal,
			onChange: handleInputChange,
			onBlur: handleInputBlur,
		},
		goToIndex: handleJump,
		backControl: {
			handle: handleBack,
			disabled: !wrapAround && atStart,
			ariaLabel: i18n.t("portal.components.useCarouselTSX.previous") as string,
		},
		nextControl: {
			handle: handleNext,
			disabled: !wrapAround && atEnd,
			ariaLabel: i18n.t("portal.components.useCarouselTSX.next") as string,
		},
	};
}

export type CarouselControl<T> = ReturnType<typeof useCarousel<T>>;
